import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom'; 
import api from '../../../api';

const ProgramsSection = () => {
  const { miniSlug, slug: collegeSlug } = useParams();
  const [sectionData, setSectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDetails, setAvailableDetails] = useState({});
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/api/${miniSlug}/${collegeSlug}/programs`);
        const data = response.data;
        setSectionData(data);

        if (Array.isArray(data?.programs) && data.programs.length > 0) {
          // check which program detail pages exist (runs in parallel)
          const checks = await Promise.all(
            data.programs.map(async (program) => {
              try {
                await api.get(`${process.env.REACT_APP_API_URL}/api/${miniSlug}/${collegeSlug}/programs/${program.link}`);
                return { slug: program.link, hasDetails: true };
              } catch {
                return { slug: program.link, hasDetails: false };
              }
            })
          );

          const detailsMap = {};
          checks.forEach(c => { detailsMap[c.slug] = c.hasDetails; });
          setAvailableDetails(detailsMap);
        }
      } catch (err) {
        setError(err.message || 'Failed to load programs');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [miniSlug, collegeSlug]);

  const toggleExpand = (slug) => {
    setExpanded(prev => ({ ...prev, [slug]: !prev[slug] }));
  };

  if (loading) return null;
  if (error) return null;
  if (!sectionData || !Array.isArray(sectionData.programs) || sectionData.programs.length === 0) return null;

  const sortedPrograms = [...sectionData.programs].sort((a, b) => {
    const ao = (typeof a.order === 'number') ? a.order : Number.MAX_SAFE_INTEGER;
    const bo = (typeof b.order === 'number') ? b.order : Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });

  return (
    <section className="courses_section pb_80" id="programs">
      <div className="container">
        <div className="sec-title text-center">
          <h2>{sectionData.title}</h2>
        </div>

        <div className="row justify-content-center">
          {sortedPrograms.map((program) => {
            const description = program.description || '';
            const words = description.split(/\s+/).filter(Boolean);
            const isLong = words.length > 40;
            const shortText = words.slice(0, 40).join(' ') + (isLong ? '...' : '');
            const showFull = !!expanded[program.link];

            const imgSrc = program.image
              ? (program.image.startsWith('http') ? program.image : `${process.env.REACT_APP_API_URL}/public${program.image}`)
              : null;

            return (
              <div 
                key={program.link}
                className="col-lg-4 col-md-6 pb-3 mb-md-0"
              >
                <article className="courses-item mb-30 hover-zoomin">
                  <div className="thumb fix">
                    {imgSrc && (
                      <img 
                        src={imgSrc} 
                        alt={program.title || ''} 
                        className="img-fluid"
                      />
                    )}
                  </div>
                  <div className="courses-content">                                    
                    <div className="cat">
                      <i className="fa fa-graduation-cap"></i> {program.level}
                    </div>
                    <div>
                      <h3>
                        {availableDetails[program.link] ? (
                          <Link to={`/${miniSlug}/${collegeSlug}/programs/${program.link}`}>
                            {program.title}
                          </Link>
                        ) : (
                          program.title
                        )}
                      </h3>

                      <p id={`program-description-${program.link}`}>
                        {showFull ? description : shortText}
                      </p>

                      {isLong && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(program.link)}
                          aria-expanded={showFull}
                          aria-controls={`program-description-${program.link}`}
                          className="view-more-btn"
                          style={{
                            color: '#007bff',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            width: 'max-content'
                          }}
                        >
                          {showFull ? 'View Less' : 'View More'}
                        </button>
                      )}

                      {availableDetails[program.link] && (
                        <Link 
                          to={`/${miniSlug}/${collegeSlug}/programs/${program.link}`} 
                          className="readmore d-block mt-2"
                        >
                          Read More <i className="fa fa-long-arrow-right"></i>
                        </Link>
                      )}
                    </div>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;